/**
 * The cockpit's judgements, pure and tested (cockpit.test.ts): whether a week is on pace, whether
 * a number may be dialled now, which rows of the board to show, today's agenda in order.
 */
import { localCallWindow, type CalendarEvent, type LeaderRow, type Task } from "@tlcrm/shared";

export type Pace = "ahead" | "on" | "behind" | "done" | "missed";

/**
 * Where a total stands against a goal and against how much of the period has gone. Behind pace
 * mid-week is a nudge (ochre); only a closed period that missed is brick.
 */
export function paceOf(value: number, goal: number, elapsed: number, closed = false): Pace {
  if (goal > 0 && value >= goal) return "done";
  if (closed) return "missed";
  const share = goal > 0 ? value / goal : 0;
  if (share >= elapsed + 0.05) return "ahead";
  if (share >= elapsed - 0.05) return "on";
  return "behind";
}

/** True when nothing should stop a dial right now: not DNC, inside calling hours. */
export function mayDial(state: string | null, doNotCall: boolean, now: number): boolean {
  if (doNotCall) return false;
  const local = localCallWindow(state, new Date(now));
  return !local || local.open;
}

/** The top three, then a window of one place either side of me, with a gap where rows are cut. */
export function boardRows(leaders: LeaderRow[], meId: string): (LeaderRow | "gap")[] {
  const mine = leaders.findIndex((l) => l.owner.id === meId);
  const keep = new Set([0, 1, 2]);
  if (mine >= 0) [mine - 1, mine, mine + 1].forEach((i) => i >= 0 && keep.add(i));
  const out: (LeaderRow | "gap")[] = [];
  let last = -1;
  leaders.forEach((l, i) => {
    if (!keep.has(i)) return;
    if (last >= 0 && i > last + 1) out.push("gap");
    out.push(l);
    last = i;
  });
  if (last < leaders.length - 1) out.push("gap");
  return out;
}

export type AgendaRow =
  { kind: "task"; at: number; task: Task } | { kind: "event"; at: number; event: CalendarEvent };

export function agendaRows(tasks: Task[], events: CalendarEvent[]): AgendaRow[] {
  const taskIds = new Set(tasks.map((t) => t.id));
  return [
    ...tasks.map((t): AgendaRow => ({
      kind: "task",
      at: t.dueAt ? Date.parse(t.dueAt) : 0,
      task: t,
    })),
    ...events
      .filter((e) => !e.taskId || !taskIds.has(e.taskId))
      .map((e): AgendaRow => ({ kind: "event", at: Date.parse(e.startsAt), event: e })),
  ].sort((a, b) => a.at - b.at);
}
